import { useMemo } from 'react';

import { orderService } from '@services/orderService';
import { RoundService } from '@services/roundService';
import type { Asset } from '@shared/types';

interface UseOptionsPricingProps {
  spotPrice: number;
  strikePrice: number;
  asset: Asset;
  volatility?: number;
}

export function useOptionsPricing({
  spotPrice,
  strikePrice,
  asset,
  volatility,
}: UseOptionsPricingProps) {
  // Get current round expiry - recalculates when round changes
  const expiryTimestamp = RoundService.getCurrentRoundExpiry();

  // Calculate option prices with memoization using OrderService
  const optionPrices = useMemo(() => {
    const callPricing = orderService.calculatePricing(
      asset,
      strikePrice,
      'CALL',
      expiryTimestamp,
      spotPrice,
      volatility,
    );

    const putPricing = orderService.calculatePricing(
      asset,
      strikePrice,
      'PUT',
      expiryTimestamp,
      spotPrice,
      volatility,
    );

    // Return in the same format as before for compatibility
    return {
      callPrice: callPricing?.mid ?? 0.01,
      putPrice: putPricing?.mid ?? 0.01,
      callBid: callPricing?.bid ?? 0.01,
      callAsk: callPricing?.ask ?? 0.01,
      callSpreadPercent: callPricing?.spreadPercent ?? 0,
      putBid: putPricing?.bid ?? 0.01,
      putAsk: putPricing?.ask ?? 0.01,
      putSpreadPercent: putPricing?.spreadPercent ?? 0,
    };
  }, [spotPrice, strikePrice, asset, expiryTimestamp, volatility]);

  const moneyness = useMemo(() => {
    if (strikePrice <= 0) {
      return 'ATM';
    }
    const ratio = spotPrice / strikePrice;
    if (ratio > 1.02) {
      return 'ITM';
    }
    if (ratio < 0.98) {
      return 'OTM';
    }
    return 'ATM';
  }, [spotPrice, strikePrice]);

  const isValid = spotPrice > 0 && strikePrice > 0;

  return {
    ...optionPrices,
    moneyness,
    isValidPricing: isValid,
    spotPrice,
  };
}

interface UseBulkOptionsPricingProps {
  spotPrice: number;
  strikePrices: number[];
  asset: Asset;
}

export function useBulkOptionsPricing({
  spotPrice,
  strikePrices,
  asset,
}: UseBulkOptionsPricingProps) {
  // Get current round expiry - recalculates when round changes
  const expiryTimestamp = RoundService.getCurrentRoundExpiry();

  // Calculate pricing for all strikes using OrderService
  const bulkPricing = useMemo(() => {
    const pricingMap = new Map<
      number,
      {
        callPrice: number;
        putPrice: number;
        callBid: number;
        callAsk: number;
        callSpreadPercent: number;
        putBid: number;
        putAsk: number;
        putSpreadPercent: number;
      }
    >();

    for (const strikePrice of strikePrices) {
      const callPricing = orderService.calculatePricing(
        asset,
        strikePrice,
        'CALL',
        expiryTimestamp,
      );

      const putPricing = orderService.calculatePricing(
        asset,
        strikePrice,
        'PUT',
        expiryTimestamp,
      );

      pricingMap.set(strikePrice, {
        callPrice: callPricing?.mid ?? 0.01,
        putPrice: putPricing?.mid ?? 0.01,
        callBid: callPricing?.bid ?? 0.01,
        callAsk: callPricing?.ask ?? 0.01,
        callSpreadPercent: callPricing?.spreadPercent ?? 0,
        putBid: putPricing?.bid ?? 0.01,
        putAsk: putPricing?.ask ?? 0.01,
        putSpreadPercent: putPricing?.spreadPercent ?? 0,
      });
    }

    return pricingMap;
  }, [spotPrice, strikePrices, asset, expiryTimestamp]);

  return bulkPricing;
}
